/**
 * Sugestão do e-mail corporativo das admissões no padrão <CPF só com números>@<domínio>
 * (liberação LIBERAR_ADMISSAO_EMAIL_CORPORATIVO_AUTOMATICO_CPF, domínio em Configurações > Geral > Admissão).
 *
 * Funções puras, sem registro no Angular. As regras de CPF válido e de montagem do e-mail
 * são as mesmas do EmailCorporativoAutomaticoCpfHelper do backend.
 */

export const CHAVE_LIBERAR_EMAIL_CORPORATIVO_AUTOMATICO_CPF: string = 'LIBERAR_ADMISSAO_EMAIL_CORPORATIVO_AUTOMATICO_CPF';
export const CHAVE_DOMINIO_EMAIL_CORPORATIVO_AUTOMATICO_CPF: string = 'DOMINIO_EMAIL_CORPORATIVO_AUTOMATICO_CPF';
export const DOMINIO_EMAIL_CORPORATIVO_AUTOMATICO_CPF_PADRAO: string = 'ana.com.vc';
/** 11 dígitos do CPF + "@" + domínio precisam caber nos 100 caracteres do e-mail corporativo */
export const DOMINIO_EMAIL_CORPORATIVO_TAMANHO_MAXIMO: number = 88;
export const REGEX_DOMINIO_EMAIL: RegExp = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

export interface ISugestaoEmailCorporativoTela {
    ativa: boolean;
    action: string;
    cpfNovo?: string | null;
    cpfAnterior?: string | null;
    emailAtual?: string | null;
    ultimaSugestao?: string | null;
    dominio: string;
}

export function somenteDigitos(valor?: string | null): string {
    return typeof valor === 'string' ? valor.replace(/\D+/g, '') : '';
}

/** 11 dígitos, não todos iguais e dígitos verificadores corretos (mesmo algoritmo da diretiva cpf-validator) */
export function cpfValido(valor?: string | null): boolean {
    const digitos: string = somenteDigitos(valor);

    if (digitos.length !== 11 || /^(\d)\1+$/.test(digitos)) {
        return false;
    }

    const digitoVerificador: (quantidade: number) => number = (quantidade: number): number => {
        let total: number = 0;
        for (let i: number = 0; i < quantidade; i++) {
            total += Number(digitos[i]) * (quantidade + 1 - i);
        }
        const resto: number = total % 11;
        return resto < 2 ? 0 : 11 - resto;
    };

    return digitoVerificador(9) === Number(digitos[9]) && digitoVerificador(10) === Number(digitos[10]);
}

export function normalizarDominio(valor?: string | null): string {
    return typeof valor === 'string' ? valor.trim().toLowerCase() : '';
}

export function dominioEmailValido(valor?: string | null): boolean {
    const dominio: string = normalizarDominio(valor);

    return dominio !== ''
        && dominio.indexOf('@') === -1
        && dominio.length <= DOMINIO_EMAIL_CORPORATIVO_TAMANHO_MAXIMO
        && REGEX_DOMINIO_EMAIL.test(dominio);
}

/** Null se o CPF não for válido */
export function montarEmailCorporativoCpf(cpf?: string | null, dominio?: string | null): string | null {
    if (!cpfValido(cpf)) {
        return null;
    }

    return `${somenteDigitos(cpf)}@${normalizarDominio(dominio) || DOMINIO_EMAIL_CORPORATIVO_AUTOMATICO_CPF_PADRAO}`;
}

/**
 * Regra da tela de admissão (funcionário e estagiário): devolve o novo e-mail corporativo, ou null para manter o atual.
 *
 * - só com a funcionalidade liberada e fora da visualização;
 * - na carga da tela (primeira execução do $watch), só a nova admissão recebe sugestão;
 * - CPF incompleto, inválido ou apagado não mexe no e-mail;
 * - só sobrescreve e-mail vazio ou que ainda é a sugestão anterior (edição manual é preservada).
 */
export function sugestaoEmailCorporativoTela(dados: ISugestaoEmailCorporativoTela): string | null {
    if (!dados.ativa || dados.action === 'show') {
        return null;
    }

    const cargaInicial: boolean = dados.cpfNovo === dados.cpfAnterior;
    if (cargaInicial && dados.action !== 'insert') {
        return null;
    }

    const sugestao: string | null = montarEmailCorporativoCpf(dados.cpfNovo, dados.dominio);
    if (!sugestao) {
        return null;
    }

    const atual: string = (dados.emailAtual || '').trim().toLowerCase();
    const podeSobrescrever: boolean = atual === ''
        || atual === montarEmailCorporativoCpf(dados.cpfAnterior, dados.dominio)
        || atual === dados.ultimaSugestao;

    return podeSobrescrever ? sugestao : null;
}
